import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useOrganization } from '@/hooks/useOrganization';
import { useBackups, useDownloadBackup, useDeleteBackup, type Backup } from '@/hooks/useBackups';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader } from '@/components/ui/loader';
import { PageHeader } from '@/components/layout/PageHeader';
import { CreateBackupModal } from '@/components/backups/CreateBackupModal';
import { CloudIcon, DownloadIcon, PlusIcon, HomeIcon, BuildingIcon, TrashIcon, MoreHorizontal } from 'lucide-react';
import { useToast } from '@/hooks/useToast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { FeatureGate } from '@/components/features/FeatureGate';
import { loadAuthGuard } from '@/lib/auth-helpers';

export const Route = createFileRoute('/_authenticated/org/$orgId/backups')({
  loader: loadAuthGuard<{ orgId: string }>(async ({ params, context, ensureQueryData, sessionReady }) => {
    const { orgId } = params;
    const { trpc } = context;

    if (!sessionReady) {
      return { orgId };
    }
    
    // Preload organizations to get organizationId from slug
    const orgsData = await ensureQueryData(
      trpc.organizations.getAll.queryOptions(undefined),
    );
    
    // Find organization by slug
    const orgs = (orgsData as { organizations?: Array<{ id: string; slug: string }> })?.organizations || [];
    const organization = orgs.find((o) => o.slug === orgId || o.id === orgId);
    const organizationId = organization?.id;
    
    // Preload backups if we have organizationId
    if (organizationId) {
      await ensureQueryData(
        trpc.backups.getByOrganization.queryOptions({ organizationId }),
      );
    }
    
    return { orgId };
  }),
  component: OrganizationBackupsPage,
});

function OrganizationBackupsPage() {
  const { orgId } = Route.useParams();
  const { organization, isLoading } = useOrganization(orgId);
  const toast = useToast();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [backupToDelete, setBackupToDelete] = useState<Backup | null>(null);

  const { data: backupsData, isLoading: backupsLoading, error: backupsError } = useBackups(organization?.slug || orgId);
  const backups: Backup[] = backupsData?.backups || [];
  
  if (backupsError) {
    console.error('Error loading backups:', backupsError);
  }
  const downloadBackupMutation = useDownloadBackup(organization?.id || '');
  const deleteBackupMutation = useDeleteBackup(organization?.id || '');

  const handleDownloadBackup = async (backup: Backup) => {
    if (!organization?.id) return;

    try {
      const result = await downloadBackupMutation.mutateAsync({
        backupId: backup.id,
        organizationId: organization.id,
      });

      // In a production environment, you would:
      // 1. Open the download URL in a new window/tab
      // 2. Or trigger a download using the filename
      
      // For now, we'll just show a toast and log the URL
      // In production, you'd actually trigger the download
      if (result.downloadUrl) {
        // Create a temporary link to trigger download
        const link = document.createElement('a');
        link.href = result.downloadUrl;
        link.download = result.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.success(`Download started: ${result.filename}`);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to download backup');
    }
  };

  const handleDelete = async () => {
    if (!backupToDelete || !organization?.id) return;
    
    try {
      await deleteBackupMutation.mutateAsync(backupToDelete.id);
      toast.success('Backup deleted successfully');
      setDeleteDialogOpen(false);
      setBackupToDelete(null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete backup');
    }
  };

  const handleDeleteClick = (backup: Backup) => {
    setBackupToDelete(backup);
    setDeleteDialogOpen(true);
  };

  if (isLoading || backupsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader variant="spinner" size="lg" color="accent" text="Loading backups..." />
      </div>
    );
  }

  const breadcrumbs = [
    {
      name: 'Dashboard',
      to: '/dashboard',
      icon: HomeIcon,
    },
    {
      name: organization?.name || 'Organization',
      to: `/org/${organization?.slug || orgId}`,
      icon: BuildingIcon,
    },
    {
      name: 'Backups',
      to: `/org/${organization?.slug || orgId}/backups`,
    },
  ];

  return (
    <FeatureGate
      organizationId={orgId}
      featureKey="backups"
      featureName="Backups"
      description="Backups allow you to protect and restore your organization's data. Upgrade your plan to access this feature."
    >
      <div className="space-y-6">
        <PageHeader
          breadcrumbs={breadcrumbs}
          title="Backups"
          subtitle={organization ? `Manage backups for ${organization.name}` : undefined}
          action={
            <Button 
              data-testid="create-backup-button"
              onClick={() => setShowCreateModal(true)}
              disabled={!organization?.id}
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Create Backup
            </Button>
          }
        />

        <Card>
        <CardHeader>
          <CardTitle>Backup History</CardTitle>
          <CardDescription>All backups of your organization's data</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {backups.map((backup: Backup) => (
              <div
                key={backup.id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  <CloudIcon className="w-5 h-5 text-gray-400" />
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">
                      {backup.name || new Date(backup.createdAt).toLocaleString()}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {backup.size || 'Unknown size'} • {new Date(backup.createdAt).toLocaleString()}
                    </p>
                    {backup.description && (
                      <p className="text-xs text-gray-400 mt-1">{backup.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Badge variant={backup.status === 'completed' ? 'default' : 'secondary'}>
                    {backup.status}
                  </Badge>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => handleDownloadBackup(backup)}
                        disabled={downloadBackupMutation.isPending || backup.status !== 'completed'}
                      >
                        <DownloadIcon className="w-4 h-4 mr-2" />
                        Download
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDeleteClick(backup)}
                        disabled={deleteBackupMutation.isPending}
                        className="text-red-600"
                      >
                        <TrashIcon className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
            {backups.length === 0 && (
              <div className="text-center py-12">
                <CloudIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No backups yet</h3>
                <p className="text-gray-500 mb-4">Create your first backup to protect your data</p>
                <Button onClick={() => setShowCreateModal(true)} disabled={!organization?.id}>
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Create Backup
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Create Backup Modal */}
      {organization?.id && (
        <CreateBackupModal
          organizationId={organization.id}
          open={showCreateModal}
          onOpenChange={setShowCreateModal}
          onSuccess={() => {
            // Modal will close automatically
          }}
        />
      )}

      {/* Delete Backup Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Backup</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{backupToDelete?.name || 'this backup'}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setBackupToDelete(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteBackupMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteBackupMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </FeatureGate>
  );
}

